/*
  Google reviews — arrows for the review row, and "Read more" on clipped reviews.
  Pairs with sections/google-reviews-custom.liquid and assets/google-reviews-custom.css.

  Both are progressive: without this file the row is still swipeable and every review shows in
  full, because the "Read more" buttons start hidden and are only revealed for cards whose text
  is actually clipped.
*/
class GoogleReviews extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;

    this.track = this.querySelector('[data-track]');
    if (!this.track) return;

    this.initialised = true;

    // Two sets of controls — the arrows beside the row on desktop, and Dawn's counter below it on
    // mobile. Only one is visible at a time, but both are always in the DOM, so everything here
    // works on all of them rather than on a single pair.
    this.prevButtons = Array.from(this.querySelectorAll('[data-prev]'));
    this.nextButtons = Array.from(this.querySelectorAll('[data-next]'));
    this.counter = this.querySelector('[data-counter]');
    this.counterCurrent = this.querySelector('[data-current]');
    this.counterTotal = this.querySelector('[data-total]');

    this.prevButtons.forEach((button) => button.addEventListener('click', () => this.scrollByStep(-1)));
    this.nextButtons.forEach((button) => button.addEventListener('click', () => this.scrollByStep(1)));

    this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true });

    // Card width is a percentage of the container, so a resize changes both the scroll step and
    // whether the text still overflows.
    this.resizeObserver = new ResizeObserver(() => {
      this.queueUpdate();
      this.syncReadMore();
    });
    this.resizeObserver.observe(this.track);

    this.setUpReadMore();
    this.updateArrows();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /* Read more ------------------------------------------------------------- */

  setUpReadMore() {
    this.readMore = Array.from(this.querySelectorAll('[data-more]')).map((button) => {
      const text = button.parentElement.querySelector('[data-text]');
      button.addEventListener('click', () => {
        const open = text.classList.toggle('gr__text--open');
        button.setAttribute('aria-expanded', String(open));
        button.textContent = open ? 'Show less' : 'Read more';
        this.queueUpdate();
      });
      return { button, text };
    });

    // Fonts land after first paint and change how many lines the text takes, so a card measured
    // too early can be judged as fitting when it does not. Re-measure once fonts are ready.
    this.syncReadMore();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.syncReadMore());
    }
  }

  // Show the button only where the text is genuinely clipped. An expanded card is left alone —
  // it always overflows by definition, and hiding its own toggle would strand it open.
  syncReadMore() {
    if (!this.readMore) return;
    this.readMore.forEach(({ button, text }) => {
      if (text.classList.contains('gr__text--open')) return;
      button.hidden = text.scrollHeight <= text.clientHeight + 1;
    });
  }

  /* Carousel -------------------------------------------------------------- */

  step() {
    const items = this.track.children;
    if (items.length < 2) return this.track.clientWidth;
    return items[1].offsetLeft - items[0].offsetLeft;
  }

  scrollByStep(direction) {
    this.track.scrollBy({ left: direction * this.step() });
  }

  queueUpdate() {
    if (this.pendingUpdate) return;
    this.pendingUpdate = true;
    requestAnimationFrame(() => {
      this.pendingUpdate = false;
      this.updateArrows();
    });
  }

  updateArrows() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const scrollable = maxScroll > 1;

    // Sub-pixel layout means scrollLeft never lands exactly on 0 or maxScroll; 1px of slack stops
    // the end arrows staying stubbornly active.
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= maxScroll - 1;

    this.prevButtons.forEach((button) => {
      button.disabled = !scrollable || atStart;
    });
    this.nextButtons.forEach((button) => {
      button.disabled = !scrollable || atEnd;
    });

    if (this.counter) this.counter.hidden = !scrollable;
    this.updateCounter();
  }

  /*
    Counts pages the way Dawn's slider does, so the numbers read the same as every other carousel:
    the total is how many scroll positions exist, not how many cards there are. With three cards
    visible out of five that is 3, not 5 — "1 / 3" matches what pressing next actually does.
  */
  updateCounter() {
    if (!this.counterCurrent || !this.counterTotal) return;

    const step = this.step();
    if (!step) return;

    const perPage = Math.max(1, Math.round(this.track.clientWidth / step));
    const total = Math.max(1, this.track.children.length - perPage + 1);
    const current = Math.min(total, Math.round(this.track.scrollLeft / step) + 1);

    this.counterCurrent.textContent = current;
    this.counterTotal.textContent = total;
  }
}

customElements.define('google-reviews', GoogleReviews);
